import { useEffect, useState } from "react";
import { api } from "../lib/api";
import AppShell from "../components/AppShell";
import { LifeBuoy, Loader2, MessageSquare, ChevronDown, ChevronUp } from "lucide-react";
import { GetHelpLink } from "../components/founder/SupportTicketModal";

const NAV = [
  {to:"/founder/dashboard",label:"Dashboard"},
  {to:"/founder/tutorial",label:"Tutorial"},
  {to:"/founder/team",label:"Team"},
  {to:"/onboarding",label:"Onboarding"},
];

const CATEGORY_LABELS = {
  document_upload: "Document Upload Issue",
  report_download: "Report Download Issue",
  ai_analysis: "Document Analysis Issue",
  general: "General",
  bug: "Bug",
  onboarding: "Onboarding",
  billing: "Billing",
  other: "Other",
};

const STATUS_STYLES = {
  open: "bg-amber-100 text-amber-800",
  replied: "bg-sky-100 text-sky-800",
  closed: "bg-neutral-200 text-neutral-600",
  resolved: "bg-emerald-100 text-emerald-800",
};

function TicketRow({ t }) {
  const [open, setOpen] = useState(false);
  const replies = t.replies || [];
  return (
    <div className="rounded-xl border border-neutral-200 bg-white" data-testid={`ticket-${t.ticket_id}`}>
      <button type="button" onClick={() => setOpen((o) => !o)}
        data-testid={`ticket-toggle-${t.ticket_id}`}
        className="flex w-full items-start justify-between gap-3 p-4 text-left">
        <div className="min-w-0">
          <p className="truncate text-sm font-bold text-neutral-900">{t.subject}</p>
          <p className="mt-0.5 text-xs text-neutral-500">
            {CATEGORY_LABELS[t.category] || t.category} · {new Date(t.created_at).toLocaleString()}
            {replies.length > 0 && ` · ${replies.length} repl${replies.length === 1 ? "y" : "ies"}`}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className={"rounded-full px-2.5 py-0.5 text-[11px] font-bold capitalize " +
            (STATUS_STYLES[t.status] || STATUS_STYLES.open)}
            data-testid={`ticket-status-${t.ticket_id}`}>
            {t.status}
          </span>
          {open ? <ChevronUp className="h-4 w-4 text-neutral-400" /> : <ChevronDown className="h-4 w-4 text-neutral-400" />}
        </div>
      </button>
      {open && (
        <div className="border-t border-neutral-100 px-4 pb-4" data-testid={`ticket-thread-${t.ticket_id}`}>
          <div className="mt-3 rounded-lg bg-neutral-50 px-3 py-2.5">
            <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-400">You wrote</p>
            <p className="mt-1 whitespace-pre-line text-sm text-neutral-800">{t.message}</p>
          </div>
          {replies.map((r, i) => (
            <div key={i} className="mt-2 rounded-lg border border-[#0A8B85]/20 bg-[#F4F8F8] px-3 py-2.5">
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#0A8B85]">
                Clinq team · {r.ts ? new Date(r.ts).toLocaleString() : ""}
              </p>
              <p className="mt-1 whitespace-pre-line text-sm text-neutral-800">{r.text}</p>
            </div>
          ))}
          {replies.length === 0 && (
            <p className="mt-2 text-xs text-neutral-500">
              No reply yet — our team will get back to you shortly (you'll also receive an email).
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default function FounderSupport() {
  const [tickets, setTickets] = useState(null);

  const load = () => api.get("/founder/assistant/tickets")
    .then((r) => {
      const rows = r.data?.tickets || [];
      setTickets(rows);
      // Mark all replies as read → clears the nav badge.
      return api.post("/founder/support-tickets/mark-read").then(() => {
        try { window.dispatchEvent(new Event("clinq:tickets-read")); } catch (_e) { /* ignore */ }
      });
    })
    .catch(() => setTickets([]));
  useEffect(() => { load(); }, []);

  return (
    <AppShell nav={NAV}>
      <div className="mx-auto w-full max-w-3xl pb-32" data-testid="founder-support-page">
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight text-neutral-900">
              <LifeBuoy className="h-6 w-6 text-[#0A8B85]" /> My support tickets
            </h1>
            <p className="mt-1 text-sm text-neutral-600">
              Track your requests and read the Clinq team's replies.
            </p>
          </div>
          <GetHelpLink issueType="document_upload" testid="support-page-new-ticket" />
        </header>

        <div className="mt-6 space-y-3">
          {tickets === null ? (
            <p className="flex items-center gap-2 text-sm text-neutral-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading your tickets…
            </p>
          ) : tickets.length === 0 ? (
            <div className="rounded-2xl border border-neutral-200 bg-neutral-50 p-8 text-center" data-testid="support-empty">
              <MessageSquare className="mx-auto h-8 w-8 text-neutral-300" />
              <p className="mt-3 text-sm font-semibold text-neutral-700">No support tickets yet</p>
              <p className="mt-1 text-xs text-neutral-500">
                Hit a snag with uploads, reports or analysing your documents? Use the
                "Having trouble? Get help" link and your ticket will show up here.
              </p>
            </div>
          ) : (
            tickets.map((t) => <TicketRow key={t.ticket_id} t={t} />)
          )}
        </div>
      </div>
    </AppShell>
  );
}
